import type { ModelChildPort, ModelMachine, ModelView } from '../../../core/domain/model';

/*
 * The rows of the Machines tile, as data (Part 5 of docs/hierarchical-models-step-4-plan.md). One
 * row for the root and one for each child. An array child is 1 row too, with the counts of its
 * machines. The row lists each machine only when it is open, so a closed array of 256 machines
 * costs 256 lookups and no cells. No Lit and no i18n here: the component gives the words, and a
 * node test can call this.
 */

/** What a machine of an array does now, as its cell shows it. 'unknown' when the runtime does not say. */
export type MachineStatus = 'running' | 'stopped' | 'faulted' | 'unknown';

/** One machine of an array. */
export interface MachineCell {
  /** `sensors/3`. */
  path: string;
  index: number;
  status: MachineStatus;
  /** Its active state ids, '' when it does not run or the model is not active. */
  state: string;
  /** Why it faulted, '' when it did not. */
  fault: string;
}

/** An array child: its count, the counts of its machines, and each machine when the row is open. */
export interface ArrayRow {
  count: number;
  /** Machines that run with no fault. 0 when the model is not active. */
  running: number;
  /** Machines with a fault, also when the model is not active: the fault tells why it stopped. */
  faulted: number;
  /** Machines whose `enabled` is not true. 0 when the model is not active. */
  stopped: number;
  /** 'list' gives each machine a line. 'grid' gives each a small cell, for a large array. */
  layout: 'list' | 'grid';
  /** Each machine by index, only when the row is open. */
  machines?: MachineCell[];
}

export interface MachineRow {
  /** The model's name for the root, the child's name otherwise. Also the key of an open row. */
  name: string;
  root: boolean;
  /** The template's name; '' for the root. */
  template: string;
  /** The active state ids of the root or a single child, '' when the model is not active. */
  state: string;
  fault: string;
  /** The ports with their wiring. For an array, the template's, with `{i}` and `{n}` in the paths as written. */
  inputs: ModelChildPort[];
  outputs: ModelChildPort[];
  /** Present for an array child only. */
  array?: ArrayRow;
}

/** The largest array that gets a line for each machine. A larger array gets a grid. */
export const LIST_UP_TO = 16;

function cellOf(path: string, index: number, x: ModelMachine | undefined, active: boolean): MachineCell {
  const fault = x?.fault ?? '';
  if (fault) return { path, index, status: 'faulted', state: '', fault };
  if (!active || !x) return { path, index, status: 'unknown', state: '', fault: '' };
  if (x.stopped) return { path, index, status: 'stopped', state: '', fault: '' };
  return { path, index, status: 'running', state: x.state, fault: '' };
}

/**
 * The rows of the Machines tile: the root first, then each child in the descriptor's order.
 * `active` is `isModelActive(m.status)`. `open` holds the names of the array rows that are open.
 */
export function machineRows(m: ModelView, active: boolean, open: ReadonlySet<string> = new Set()): MachineRow[] {
  const live = new Map<string, ModelMachine>(m.machines.map((x) => [x.path, x]));
  const root = live.get('');
  const rows: MachineRow[] = [
    {
      name: m.name,
      root: true,
      template: '',
      state: active ? (root?.state ?? m.currentState) : '',
      fault: root?.fault ?? '',
      // The root's own wiring (Part 4 of the Step 3 plan): an input it does not connect has no value.
      inputs: m.inputs.map((name) => m.wiring?.inputs.find((p) => p.name === name) ?? { name, path: '' }),
      outputs: m.outputs.map((name) => m.wiring?.outputs.find((p) => p.name === name) ?? { name, path: '' }),
    },
  ];
  for (const c of m.children) {
    const row: MachineRow = { name: c.name, root: false, template: c.template, state: '', fault: '', inputs: c.inputs, outputs: c.outputs };
    if (c.count === undefined) {
      const x = live.get(c.name);
      row.state = active ? (x?.state ?? '') : '';
      row.fault = x?.fault ?? '';
    } else {
      const array: ArrayRow = { count: c.count, running: 0, faulted: 0, stopped: 0, layout: c.count > LIST_UP_TO ? 'grid' : 'list' };
      const cells: MachineCell[] | undefined = open.has(c.name) ? [] : undefined;
      for (let i = 0; i < c.count; i++) {
        const path = `${c.name}/${i}`;
        const cell = cellOf(path, i, live.get(path), active);
        if (cell.status === 'running') array.running++;
        else if (cell.status === 'faulted') array.faulted++;
        else if (cell.status === 'stopped') array.stopped++;
        cells?.push(cell);
      }
      if (cells) array.machines = cells;
      row.array = array;
    }
    rows.push(row);
  }
  return rows;
}
